import { format } from "date-fns";
import React, { RefObject, useState } from "react";
import MessageComposer from "./MessageComposer";
import QuickResponseTemplates from "./QuickResponseTemplates";

interface ChatMessage {
	id: string;
	sender: "agent" | "buyer";
	senderName: string;
	content: string;
	timestamp: Date;
	type: "text" | "file";
	fileName?: string;
	fileSize?: number;
	read?: boolean;
}

interface Conversation {
	id: string;
	buyer_name: string;
	propertyInterest: string;
}

interface Template {
	id: number;
	title: string;
	content: string;
}

interface ChatAreaProps {
	conversation: Conversation | null;
	messages: ChatMessage[];
	onSendMessage: (data: { text?: string; file?: File }) => void;
	isTyping: boolean;
	onShowBuyerPanel: () => void;
	showBuyerPanel: boolean;
	messagesEndRef: RefObject<HTMLDivElement>;
	onBack?: () => void; // Mobile back button handler
}

const ChatArea: React.FC<ChatAreaProps> = ({
	conversation,
	messages,
	onSendMessage,
	isTyping,
	onShowBuyerPanel,
	showBuyerPanel,
	messagesEndRef,
	onBack,
}) => {
	const [showTemplates, setShowTemplates] = useState(false);

	const handleTemplateSelect = (template: Template) => {
		onSendMessage({ text: template?.content });
		setShowTemplates(false);
	};

	const renderMessage = (message: ChatMessage) => {
		const isOwnMessage = message?.sender === "agent";

		return (
			<div
				key={message?.id}
				className={`flex ${
					isOwnMessage ? "justify-end" : "justify-start"
				} mb-4`}
			>
				<div
					className={`max-w-xs lg:max-w-md xl:max-w-lg ${
						isOwnMessage ? "" : "order-2"
					}`}
				>
					{/* Sender Name */}
					{!isOwnMessage && (
						<div className="text-xs text-muted-foreground mb-1 px-3">
							{message?.senderName}
						</div>
					)}

					{/* Message Bubble */}
					<div
						className={`px-4 py-2 rounded-2xl ${
							isOwnMessage
								? "bg-primary text-primary-foreground rounded-br-md"
								: "bg-muted text-foreground rounded-bl-md"
						}`}
					>
						{message?.type === "file" ? (
							<div className="flex items-center space-x-2">
								<svg
									className="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
									/>
								</svg>
								<div className="flex-1">
									{/* Extract file URL from message content */}
									{(() => {
										const fileLinkMatch = message.content.match(/\[File: ([^\]]+)\]\(([^)]+)\)/);
										if (fileLinkMatch) {
											const fileName = fileLinkMatch[1];
											const fileUrl = fileLinkMatch[2];
											return (
												<a
													href={fileUrl}
													target="_blank"
													rel="noopener noreferrer"
													className="hover:underline"
												>
													<div className="text-sm font-medium">{fileName}</div>
													<div className="text-xs opacity-75">
														{message?.fileSize
															? `${(message?.fileSize / 1024)?.toFixed(1)} KB`
															: "Click to view file"}
													</div>
												</a>
											);
										}
										return (
											<div>
												<div className="text-sm font-medium">{message?.fileName || "File"}</div>
												<div className="text-xs opacity-75">
													{message?.fileSize
														? `${(message?.fileSize / 1024)?.toFixed(1)} KB`
														: "File"}
												</div>
											</div>
										);
									})()}
								</div>
							</div>
						) : (
							<p className="text-sm whitespace-pre-wrap">{message?.content}</p>
						)}
					</div>

					{/* Timestamp and Status */}
					<div
						className={`flex items-center mt-1 px-3 space-x-2 ${
							isOwnMessage ? "justify-end" : ""
						}`}
					>
						<span className="text-xs text-muted-foreground">
							{format(message?.timestamp, "HH:mm")}
						</span>
						{isOwnMessage && (
							<div className="flex space-x-1">
								<svg
									className={`w-3 h-3 ${
										message?.read ? "text-primary" : "text-muted-foreground"
									}`}
									fill="currentColor"
									viewBox="0 0 20 20"
								>
									<path
										fillRule="evenodd"
										d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
										clipRule="evenodd"
									/>
								</svg>
								{message?.read && (
									<svg
										className="w-3 h-3 text-primary"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fillRule="evenodd"
											d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
											clipRule="evenodd"
										/>
									</svg>
								)}
							</div>
						)}
					</div>
				</div>
				{/* Avatar for incoming messages */}
				{!isOwnMessage && (
					<div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center ml-2 order-1">
						<span className="text-xs font-medium text-muted-foreground">
							{message?.senderName?.charAt(0)?.toUpperCase()}
						</span>
					</div>
				)}
			</div>
		);
	};

	return (
		<div className="flex flex-col h-full">
			{/* Chat Header */}
			<div className="flex items-center justify-between p-4 border-b border-border bg-card">
			{/* Mobile Back Button */}
			{onBack && (
				<button
					onClick={onBack}
					className="lg:hidden p-2 hover:bg-muted rounded-lg transition-colors"
					title="Back to conversations"
				>
					<svg
						className="w-5 h-5"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M15 19l-7-7 7-7"
						/>
					</svg>
				</button>
			)}
				<div className="flex items-center space-x-3">
					<div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
						<span className="text-sm font-medium text-muted-foreground">
							{conversation?.buyer_name?.charAt(0)?.toUpperCase()}
						</span>
					</div>
					<div>
						<h3 className="font-medium text-foreground">
							{conversation?.buyer_name}
						</h3>
						<p className="text-sm text-muted-foreground">
							{conversation?.propertyInterest}
						</p>
					</div>
				</div>

				<div className="flex items-center space-x-2">
					{/* Quick Templates Button */}
					<button
						onClick={() => setShowTemplates(!showTemplates)}
						className={`p-2 rounded-lg transition-colors ${
							showTemplates
								? "bg-primary text-primary-foreground"
								: "hover:bg-muted"
						}`}
						title="Quick responses"
					>
						<svg
							className="w-4 h-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M13 10V3L4 14h7v7l9-11h-7z"
							/>
						</svg>
					</button>

					{/* Buyer Info Panel Toggle */}
					<button
						onClick={onShowBuyerPanel}
						className={`p-2 rounded-lg transition-colors ${
							showBuyerPanel
								? "bg-primary text-primary-foreground"
								: "hover:bg-muted"
						}`}
						title="Buyer information"
					>
						<svg
							className="w-4 h-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
							/>
						</svg>
					</button>

					{/* Video Call */}
					<button
						className="p-2 hover:bg-muted rounded-lg transition-colors"
						title="Video call"
					>
						<svg
							className="w-4 h-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
							/>
						</svg>
					</button>

					{/* Phone Call */}
					<button
						className="p-2 hover:bg-muted rounded-lg transition-colors"
						title="Phone call"
					>
						<svg
							className="w-4 h-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
							/>
						</svg>
					</button>
				</div>
			</div>
			{/* Quick Templates Panel */}
			{showTemplates && (
				<QuickResponseTemplates
					onTemplateSelect={handleTemplateSelect}
					onClose={() => setShowTemplates(false)}
				/>
			)}
			{/* Messages Area */}
			<div className="flex-1 overflow-y-auto p-4 bg-background">
				{messages?.length === 0 ? (
					<div className="flex items-center justify-center h-full">
						<div className="text-center">
							<div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
								<svg
									className="w-8 h-8 text-muted-foreground"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.959 8.959 0 01-4.906-1.456L3 21l2.456-5.094A8.959 8.959 0 013 12c0-4.418 3.582-8 8-8s8 3.582 8 8z"
									/>
								</svg>
							</div>
							<h3 className="text-lg font-medium text-foreground mb-2">
								Start the conversation
							</h3>
							<p className="text-muted-foreground">
								Send a message to {conversation?.buyer_name} to begin chatting
							</p>
						</div>
					</div>
				) : (
					<>
						{messages?.map(renderMessage)}

						{/* Typing Indicator */}
						{isTyping && (
							<div className="flex justify-start mb-4">
								<div className="flex items-center space-x-2">
									<div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
										<span className="text-xs font-medium text-muted-foreground">
											{conversation?.buyer_name?.charAt(0)?.toUpperCase()}
										</span>
									</div>
									<div className="bg-muted px-4 py-2 rounded-2xl rounded-bl-md">
										<div className="flex space-x-1">
											<div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
											<div
												className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"
												style={{ animationDelay: "0.1s" }}
											></div>
											<div
												className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"
												style={{ animationDelay: "0.2s" }}
											></div>
										</div>
									</div>
								</div>
							</div>
						)}

						<div ref={messagesEndRef} />
					</>
				)}
			</div>
			{/* Message Composer */}
			<MessageComposer onSendMessage={onSendMessage} />
		</div>
	);
};

export default ChatArea;
